import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { East, ExpandMore } from '@mui/icons-material';

import './Home.css';
import './MyBuilds.css';
import '../components/aiChat/AiChatPanel.css';
import HeroSolverDiagram from '../components/hero/HeroSolverDiagram';
import BgPattern from '../components/decor/BgPattern';
import { HOME_BG_PATTERN_ENABLED } from '../components/decor/patterns';
import { HomeCommunitySection } from '../components/community/HomeCommunitySection';
import { loadConversations } from '../services/chatHistoryStorage';
import { t } from '../components/aiChat/aiChatUtils';

// "Theorycraft" stays an unconjugated English verb in every language, the way
// the French copy already treats it.
const TXT_HERO_TITLE = {
    fr: 'Theorycraft le stuff optimal.',
    en: 'Theorycraft the optimal build.',
    de: 'Theorycraft den optimalen Build.',
    es: 'Theorycraft la build óptima.',
    pt: 'Theorycraft a build ideal.',
};
const TXT_GO_TO_GENERATOR = {
    fr: 'Accéder au générateur',
    en: 'Go to generator',
    de: 'Zum Generator',
    es: 'Ir al generador',
    pt: 'Ir para o gerador',
};
const TXT_AI_ASSISTANT = { fr: 'Assistant IA', en: 'AI assistant', de: 'KI-Assistent', es: 'Asistente IA', pt: 'Assistente IA' };
const TXT_EN_US = { fr: 'fr-FR', en: 'en-US', de: 'de-DE', es: 'es-ES', pt: 'pt-BR' };
const TXT_OVERVIEW = { fr: 'Présentation', en: 'Overview', de: 'Überblick', es: 'Presentación', pt: 'Visão geral' };
const TXT_EXPERIMENTAL = { fr: 'expérimental', en: 'experimental', de: 'experimentell', es: 'experimental', pt: 'experimental' };

// Toggle the "constraints -> AI solver -> build" hero diagram (right column).
const SHOW_HOME_HERO_DIAGRAM = false;

const HOME_SECTIONS = [
    {
        id: 'generator',
        href: 'gen',
        title: { fr: 'Générateur', en: 'Generator', de: 'Generator', es: 'Generador', pt: 'Gerador' },
        kicker: {
            fr: 'Editeur de stuffs avancé',
            en: 'Advanced build editor',
            de: 'Erweiterter Build-Editor',
            es: 'Editor de builds avanzado',
            pt: 'Editor de builds avançado',
        },
        body: {
            fr: 'Choisis ta classe, ton niveau et tes critères de stats ou des sorts à maximiser. Le générateur explore une multitude de combinaisons d\'items et de panoplies dans le temps imparti et retourne le meilleur stuff trouvé. ',
            en: 'Choose your class, your level and your stat criteria or the spells to maximize. The generator explores a multitude of item and set combinations in the allotted time and returns the best build it finds. ',
            de: 'Wähle deine Klasse, deine Stufe und deine Stat- oder Zauberkriterien. Der Generator durchsucht in der vorgegebenen Zeit unzählige Gegenstands- und Set-Kombinationen und liefert den besten gefundenen Build. ',
            es: 'Elige tu clase, tu nivel y tus criterios de stats o los hechizos a maximizar. El generador explora multitud de combinaciones de objetos y sets en el tiempo asignado y devuelve la mejor build encontrada. ',
            pt: 'Escolha a sua classe, o seu nível e os seus critérios de stats ou os feitiços a maximizar. O gerador explora várias combinações de itens e conjuntos no tempo disponível e retorna a melhor build encontrada. ',
        },
        extraBody: [
            {
                fr: 'De nombreuses fonctionnalités permettent d\'ajuster le générateur à tes besoins. Tu peux verrouiller des emplacements, exclure des items ou encore activer la défense automatique qui incite le générateur à produire des stuffs avec PV et résistances, sans avoir besoin de le spécifier manuellement.',
                en: 'Many features allow you to tailor the generator to your needs. You can lock slots, exclude items, or activate automatic defense which encourages the generator to produce builds with HP and resistances, without having to specify them manually.',
                de: 'Zahlreiche Funktionen erlauben es, den Generator an deine Bedürfnisse anzupassen. Du kannst Slots sperren, Gegenstände ausschließen oder die automatische Verteidigung aktivieren, die den Generator dazu bringt, Builds mit LP und Resistenzen zu erzeugen, ohne dass du sie manuell angeben musst.',
                es: 'Muchas funciones permiten ajustar el generador a tus necesidades. Puedes bloquear espacios, excluir objetos o activar la defensa automática, que incita al generador a producir builds con PdV y resistencias, sin tener que especificarlo manualmente.',
                pt: 'Muitos recursos permitem ajustar o gerador às suas necessidades. Você pode travar espaços, excluir itens ou ativar a defesa automática, que incentiva o gerador a produzir builds com PV e resistências, sem precisar especificar manualmente.',
            },
            {
                fr: 'Tu peux également utiliser le générateur comme un éditeur d\'équipement classique et partager le résultat.',
                en: 'You can also use the generator as a classic equipment editor and share the result.',
                de: 'Du kannst den Generator auch als klassischen Ausrüstungseditor nutzen und das Ergebnis teilen.',
                es: 'También puedes usar el generador como un editor de equipo clásico y compartir el resultado.',
                pt: 'Você também pode usar o gerador como um editor de equipamento clássico e compartilhar o resultado.',
            },
        ],
        cta: {
            fr: 'Essayer le générateur',
            en: 'Try the generator',
            de: 'Generator ausprobieren',
            es: 'Probar el generador',
            pt: 'Experimentar o gerador',
        },
        placeholder: {
            fr: 'Image générateur',
            en: 'Generator image',
            de: 'Generator-Bild',
            es: 'Imagen del generador',
            pt: 'Imagem do gerador',
        },
    },
    {
        id: 'encyclopedia',
        href: 'encyclopedia',
        title: { fr: 'Encyclopédie', en: 'Encyclopedia', de: 'Enzyklopädie', es: 'Enciclopedia', pt: 'Enciclopédia' },
        kicker: {
            fr: 'Recherche les items et panoplies',
            en: 'Search items and sets',
            de: 'Gegenstände und Sets durchsuchen',
            es: 'Busca los objetos y sets',
            pt: 'Pesquise os itens e conjuntos',
        },
        body: {
            fr: 'Parcours tous les items et panoplies du jeu et ajoute les items à l\'équipement en cours d\'édition.',
            en: 'Browse every item and set in the game and add items to the equipment you are editing.',
            de: 'Durchstöbere alle Gegenstände und Sets des Spiels und füge Gegenstände der gerade bearbeiteten Ausrüstung hinzu.',
            es: 'Recorre todos los objetos y sets del juego y añade objetos al equipo que estás editando.',
            pt: 'Percorra todos os itens e conjuntos do jogo e adicione itens ao equipamento em edição.',
        },
        cta: {
            fr: 'Consulter l’encyclopédie',
            en: 'Open the encyclopedia',
            de: 'Enzyklopädie öffnen',
            es: 'Consultar la enciclopedia',
            pt: 'Consultar a enciclopédia',
        },
        placeholder: {
            fr: 'Image encyclopédie',
            en: 'Encyclopedia image',
            de: 'Enzyklopädie-Bild',
            es: 'Imagen de la enciclopedia',
            pt: 'Imagem da enciclopédia',
        },
    },
    {
        id: 'my-builds',
        href: 'my-builds',
        title: { fr: 'Mes stuffs', en: 'My builds', de: 'Meine Builds', es: 'Mis builds', pt: 'Minhas builds' },
        kicker: {
            fr: 'Ta bibliothèque perso',
            en: 'Your saved builds',
            de: 'Deine persönliche Bibliothek',
            es: 'Tu biblioteca personal',
            pt: 'A sua biblioteca pessoal',
        },
        body: {
            fr: 'Retrouve les stuffs que tu as créés et ceux que tu as likés. Filtre par classe et par tags, duplique ou supprime un stuff. Ta bibliothèque est synchronisée via ton compte Discord.',
            en: 'Find the builds you created and the ones you liked. Filter by class and tags, duplicate or delete a build. Your library is synced via your Discord account.',
            de: 'Finde die Builds wieder, die du erstellt oder gelikt hast. Filtere nach Klasse und Tags, dupliziere oder lösche einen Build. Deine Bibliothek wird über dein Discord-Konto synchronisiert.',
            es: 'Encuentra las builds que has creado y las que te han gustado. Filtra por clase y por tags, duplica o elimina una build. Tu biblioteca se sincroniza con tu cuenta de Discord.',
            pt: 'Encontre as builds que você criou e as que curtiu. Filtre por classe e por tags, duplique ou exclua uma build. Sua biblioteca é sincronizada pela sua conta Discord.',
        },
        cta: {
            fr: 'Voir mes stuffs',
            en: 'View my builds',
            de: 'Meine Builds ansehen',
            es: 'Ver mis builds',
            pt: 'Ver as minhas builds',
        },
    },
];

// The hero clip is a screen recording of the generator, so its on-screen text is
// baked in. Only fr and en are recorded (demo/: `npm run record:hero[:en]`);
// every other language gets the English one rather than French words it cannot read.
const HERO_DEMO_LANGS = ['fr', 'en'];

const Home = ({ lang = 'fr' }) => {
    const heroDemoLang = HERO_DEMO_LANGS.includes(lang) ? lang : 'en';
    const navigate = useNavigate();
    const [input, setInput] = useState('');
    const [historyExpanded, setHistoryExpanded] = useState(false);
    const [savedConversations] = useState(() => loadConversations());
    const featureSectionsRef = useRef(null);

    useEffect(() => {
        const root = featureSectionsRef.current;
        if (!root) return;
        const composites = root.querySelectorAll('.home-feature-img-composite');
        if (!composites.length) return;
        const mo = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (mo.matches) return;
        const scrollRoot = document.querySelector('.main-content') || null;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add('is-in-view');
                        io.unobserve(e.target);
                    }
                });
            },
            { root: scrollRoot, threshold: 0.12 }
        );
        composites.forEach((c) => io.observe(c));
        return () => io.disconnect();
    }, []);

    const startNewConversation = (rawPrompt) => {
        const prompt = String(rawPrompt || '').trim();
        if (!prompt) return;
        const id = `conv-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        navigate(`/${lang}/chat/${id}`, { state: { initialPrompt: prompt } });
    };

    const resumeConversation = (conv) => {
        navigate(`/${lang}/chat/${conv.id}`);
    };

    return (
        <div className="home-root column">
            <main className="home-hero column">
                <section className="home-hero-grid">
                    <div className="home-hero-left column">
                        <div className="home-hero-logo" aria-hidden="true">
                            <img src="/img/dofgen_logo.svg" alt="" />
                        </div>

                        <h1 className="home-hero-title">{TXT_HERO_TITLE[lang] || TXT_HERO_TITLE.en}</h1>

                        <p className="home-hero-subtitle">{t(lang, 'homeHeroSubtitle')}</p>

                        <Link to={`/${lang}/gen`} className="home-input-cta-link">
                            <button type="button" className="primary alt home-input-cta-btn">
                                {TXT_GO_TO_GENERATOR[lang] || TXT_GO_TO_GENERATOR.en}
                                <East fontSize="small" />
                            </button>
                        </Link>
                    </div>

                    {SHOW_HOME_HERO_DIAGRAM ? (
                        <div className="home-hero-right" aria-hidden="true">
                            <HeroSolverDiagram lang={lang} />
                        </div>
                    ) : (
                        <div className="home-hero-right" aria-hidden="true">
                            <video
                                key={heroDemoLang}
                                className="home-hero-video"
                                autoPlay
                                loop
                                muted
                                playsInline
                                preload="metadata"
                            >
                                <source src={`/video/hero-demo-${heroDemoLang}.webm`} type="video/webm" />
                                <source src={`/video/hero-demo-${heroDemoLang}.mp4`} type="video/mp4" />
                            </video>
                        </div>
                    )}
                </section>
            </main>
            <HomeCommunitySection lang={lang} />
            <section ref={featureSectionsRef} className="home-feature-sections column" aria-label={TXT_OVERVIEW[lang] || TXT_OVERVIEW.en}>
                {HOME_SECTIONS.map((section) => (
                    <section key={section.id} className="home-feature-section">
                        <Link
                            to={`/${lang}/${section.href}`}
                            className={
                                section.id === 'generator'
                                    ? 'home-feature-image home-feature-image--generator home-feature-image-link'
                                    : section.id === 'encyclopedia'
                                        ? 'home-feature-image home-feature-image--encyclopedia home-feature-image-link'
                                        : 'home-feature-image home-feature-image--my-builds home-feature-image-link'
                            }
                            aria-label={section.cta[lang] || section.cta.en}
                        >
                            {section.id === 'generator' ? (
                                <div className="home-feature-img-composite">
                                    <img
                                        src="/img/home_generator.jpg"
                                        alt=""
                                        className="home-feature-img-main"
                                    />
                                    <img
                                        src="/img/home_generator_bar.jpg"
                                        alt=""
                                        className="home-feature-img-bar home-feature-img-bar--generator"
                                    />
                                </div>
                            ) : section.id === 'encyclopedia' ? (
                                <div className="home-feature-img-composite">
                                    <img
                                        src="/img/home_encylopedia.jpg"
                                        alt=""
                                        className="home-feature-img-main"
                                    />
                                    <img
                                        src="/img/home_encylopedia_bar.jpg"
                                        alt=""
                                        className="home-feature-img-bar home-feature-img-bar--encyclopedia"
                                    />
                                </div>
                            ) : (
                                <div className="home-feature-img-composite">
                                    <img
                                        src="/img/home_mybuilds.jpg"
                                        alt=""
                                        className="home-feature-img-main"
                                    />
                                </div>
                            )}
                        </Link>
                        <div className="home-feature-copy column">
                            <h1>{section.title[lang] || section.title.en}</h1>
                            <h4>{section.kicker[lang] || section.kicker.en}</h4>
                            <p>{section.body[lang] || section.body.en}</p>
                            {(section.extraBody || [])
                                .map((paragraph) => paragraph[lang] || paragraph.en)
                                .filter((paragraph) => String(paragraph || '').trim())
                                .map((paragraph, index) => (
                                    <p key={`${section.id}-extra-${index}`}>{paragraph}</p>
                                ))}
                            {section.cta && section.href && (
                                <Link to={`/${lang}/${section.href}`} className="home-feature-cta-link">
                                    <button type="button" className="primary alt">
                                        {section.cta[lang] || section.cta.en}
                                        <East fontSize="small" />
                                    </button>
                                </Link>
                            )}
                        </div>
                    </section>
                ))}
            </section>
            <section
                className="home-chat-section ai-chat-panel is-empty home-chat-panel"
                aria-label={TXT_AI_ASSISTANT[lang] || TXT_AI_ASSISTANT.en}
            >
                {HOME_BG_PATTERN_ENABLED && <BgPattern />}
                <div className="home-chat-input-group">
                    {/* <p className="home-chat-title">{t(lang, 'homeChatTitle')}</p> */}
                    <div className="home-chat-badge-row">
                        <div className="app-version-badge">{TXT_EXPERIMENTAL[lang] || TXT_EXPERIMENTAL.en}</div>
                    </div>
                    <div className="ai-chat-input-row">
                        <div className="ai-chat-input-shell">
                            <input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder={t(lang, 'placeholder')}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        startNewConversation(input);
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className="ai-chat-send-btn primary"
                                onClick={() => startNewConversation(input)}
                                disabled={!input.trim()}
                                aria-label={t(lang, 'send')}
                            >
                                <East fontSize="small" />
                            </button>
                        </div>
                    </div>

                    {savedConversations.length > 0 && (
                        <div className={`ai-chat-history${historyExpanded ? ' is-expanded' : ''}`}>
                            <button
                                type="button"
                                className="ai-chat-history-toggle"
                                onClick={() => setHistoryExpanded((open) => !open)}
                                aria-expanded={historyExpanded}
                            >
                                <span className="ai-chat-history-label">{t(lang, 'resumeConversation')}</span>
                                <ExpandMore
                                    className={`ai-chat-history-chevron${historyExpanded ? ' is-expanded' : ''}`}
                                    fontSize="inherit"
                                    aria-hidden="true"
                                />
                            </button>
                            {historyExpanded && (
                                <div className="ai-chat-history-list">
                                    {savedConversations.map((conv) => (
                                        <button
                                            key={conv.id}
                                            type="button"
                                            className="ai-chat-history-item"
                                            onClick={() => resumeConversation(conv)}
                                        >
                                            <span className="ai-chat-history-item-label">{conv.label}</span>
                                            <span className="ai-chat-history-item-date">
                                                {new Date(conv.savedAt).toLocaleDateString(
                                                    TXT_EN_US[lang] || TXT_EN_US.en,
                                                    { day: 'numeric', month: 'short' }
                                                )}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Home;
